import { MAP_HEX_RADIUS } from "../config/balance.ts";
import type { TileCoordinates } from "../worldState.ts";

export const AXIAL_DIRECTION_OFFSETS: readonly TileCoordinates[] = [
  { q: 1, r: 0 },
  { q: 1, r: -1 },
  { q: 0, r: -1 },
  { q: -1, r: 0 },
  { q: -1, r: 1 },
  { q: 0, r: 1 },
];

export const DIRECTION_KEY_COUNT = AXIAL_DIRECTION_OFFSETS.length;

export function parseTileKey(key: string): TileCoordinates | null {
  const comma = key.indexOf(",");
  if (comma === -1) {
    return null;
  }

  const q = Number(key.slice(0, comma));
  const r = Number(key.slice(comma + 1));
  if (!Number.isFinite(q) || !Number.isFinite(r)) {
    return null;
  }

  return { q, r };
}

export function hexDistance(a: TileCoordinates, b: TileCoordinates): number {
  const dq = a.q - b.q;
  const dr = a.r - b.r;
  return (Math.abs(dq) + Math.abs(dq + dr) + Math.abs(dr)) / 2;
}

export function hexTileCount(radius: number): number {
  return 3 * radius * (radius + 1) + 1;
}

export function isInHexShape(
  q: number,
  r: number,
  radius = MAP_HEX_RADIUS,
): boolean {
  return Math.max(Math.abs(q), Math.abs(r), Math.abs(q + r)) <= radius;
}

export function buildHexGrid(radius = MAP_HEX_RADIUS): TileCoordinates[] {
  const coords: TileCoordinates[] = [];

  for (let q = -radius; q <= radius; q++) {
    const rMin = Math.max(-radius, -q - radius);
    const rMax = Math.min(radius, -q + radius);
    for (let r = rMin; r <= rMax; r++) {
      coords.push({ q, r });
    }
  }

  return coords;
}

export function hexNeighbors(
  q: number,
  r: number,
  radius = MAP_HEX_RADIUS,
): TileCoordinates[] {
  return AXIAL_DIRECTION_OFFSETS
    .map(({ q: dq, r: dr }) => ({ q: q + dq, r: r + dr }))
    .filter(({ q: nq, r: nr }) => isInHexShape(nq, nr, radius));
}

export function areAdjacent(
  a: TileCoordinates,
  b: TileCoordinates,
  radius = MAP_HEX_RADIUS,
): boolean {
  const dq = b.q - a.q;
  const dr = b.r - a.r;
  const isNeighborOffset = AXIAL_DIRECTION_OFFSETS.some(
    (offset) => offset.q === dq && offset.r === dr,
  );

  return isNeighborOffset && isInHexShape(b.q, b.r, radius);
}
